import { MouseEventHandler, ReactNode } from 'react';

export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'dark';

type Props = {
  text: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset' | undefined;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  colorStyle?: 'default' | 'outline' | 'solid';
  color?: ButtonColor;
  icon?: {
    pos: 'left' | 'right';
    icon?: string | ReactNode;
  };
  onClick: MouseEventHandler<HTMLButtonElement>;

  block?: boolean;
  rounded?: boolean;
  disabled?: boolean;
  withIcon?: boolean;
  isLastQuestion?: boolean;
};

export default function Button({
  text,
  className = '',
  type = 'button',
  size = 'md',
  colorStyle = 'default',
  color = 'primary',
  icon,
  onClick,
  block = false,
  rounded = false,
  disabled = false,
  withIcon = true,
  isLastQuestion = false,
}: Props) {
  let colorMapping: { [key: string]: string } = {
    primary: '',
    secondary: '',
    success: '',
    error: '',
    warning: '',
    dark: '',
  };
  switch (colorStyle) {
    case 'default':
      colorMapping = {
        primary:
          'bg-gradient-to-r hover:bg-gradient-to-l from-[#76a9fa] to-[#5850ec] text-white border-none ',
        secondary: 'btn-secondary',
        success:
          'bg-gradient-to-r hover:bg-gradient-to-l from-[#3ad584] to-[#38b768] text-white',
        error:
          'bg-gradient-to-r hover:bg-gradient-to-l from-[#fb4a4a] to-[#e40505] text-white',
        warning:
          'bg-gradient-to-r hover:bg-gradient-to-l from-[#f5ee30] to-[#fbb83f] text-[#1e429f] border-none',
        dark: 'bg-gray-500 text-white',
      };
      break;
    case 'outline':
      colorMapping = {
        primary: 'btn-outline-primary',
        secondary: 'btn-outline-secondary',
        success: 'btn-outline-success',
        error: 'btn-outline-error',
        warning: 'btn-outline-warning',
        dark: '',
      };
      break;
    case 'solid':
      colorMapping = {
        primary: `btn-solid-primary ${
          isLastQuestion
            ? 'bg-asi-green-primary hover:bg-asi-green-secondary'
            : 'bg-asi-blue-primary hover:bg-asi-blue-light'
        } text-white`,
        secondary: 'btn-solid-secondary',
        success: 'btn-solid-success',
        error:
          'btn-solid-error bg-asi-red-secondary hover:bg-asi-red-light text-white',
        warning: 'btn-solid-warning',
        dark: '',
      };
      break;
  }

  const sizeMapping: { [key: string]: string } = {
    xs: 'btn-xs',
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg',
    xl: 'btn-xl',
  };

  const classGroup =
    'btn ' +
    (color ? `${colorMapping[color]} ` : '') +
    (size ? `${sizeMapping[size]} ` : '') +
    (block ? 'btn-block ' : '') +
    (rounded ? 'btn-rounded ' : '');

  return (
    <button
      className={`rounded-full font-montserrat ${classGroup} ${className}`}
      type={type}
      onClick={onClick}
      disabled={disabled}
    >
      <div
        className={'cursor-pointer flex gap-x-2 justify-center items-center'}
      >
        {withIcon && icon && icon.pos === 'left' && (
          <span className="flex justify-center items-center">
            {typeof icon.icon === 'string' ? (
              <i className={icon.icon}></i>
            ) : (
              icon.icon
            )}
          </span>
        )}
        <span className="flex justify-center items-center font-bold ">
          {text}
        </span>
        {withIcon && icon && icon.pos === 'right' && (
          <span className="flex justify-center items-center">
            {typeof icon.icon === 'string' ? (
              <i className={icon.icon}></i>
            ) : (
              icon.icon
            )}
          </span>
        )}
      </div>
    </button>
  );
}
